import { Dispatch } from "redux";
import axios from "axios";
import { generateCodeChallenge } from "../utils/securityUtils";
import { ErrorType } from "../_shared/domain/errorType";
import {
  AUTH1302,
  POLL_PENDING,
} from "@delorean/_shared/constants/serviceStatusCode";
import { useCookies } from "react-cookie";
import { LOGIN_CHANNEL } from "@delorean/_shared/constants";
import { boolean } from "zod";
import { getApiEndpoints, getEnvVar } from "@delorean/utils/env-utils";

export const AUTHORIZE_REQUEST = "AUTHORIZE_REQUEST";
export const AUTHORIZE_SUCCESS = "AUTHORIZE_SUCCESS";
export const AUTHORIZE_FAILURE = "AUTHORIZE_FAILURE";
export const AUTHORIZE_REQUEST_EXPIRED = "AUTHORIZE_REQUEST_EXPIRED";
export const AUTHORIZE_SESSION_EXPIRED = "AUTHORIZE_SESSION_EXPIRED";
export const SET_POLL_ABORT_CONTROLLER = "SET_POLL_ABORT_CONTROLLER";
export const ABORT_POLLING = "ABORT_POLLING";

interface AuthorizeRequestAction {
  type: typeof AUTHORIZE_REQUEST;
}

interface AuthorizeSuccessAction {
  type: typeof AUTHORIZE_SUCCESS;
  payload: any;
}

interface AuthorizeFailureAction {
  type: typeof AUTHORIZE_FAILURE;
  error: string;
}

interface AuthorizeRequestExpiredAction {
  type: typeof AUTHORIZE_REQUEST_EXPIRED;
}

interface AuthorizeSessionExpiredAction {
  type: typeof AUTHORIZE_SESSION_EXPIRED;
  error: string;
}

interface SetPollAbortControllerAction {
  type: typeof SET_POLL_ABORT_CONTROLLER;
  payload: AbortController;
}

interface AbortPollingAction {
  type: typeof ABORT_POLLING;
}

export type AuthorizeActionTypes =
  | AuthorizeRequestAction
  | AuthorizeSuccessAction
  | AuthorizeFailureAction
  | AuthorizeRequestExpiredAction
  | AuthorizeSessionExpiredAction
  | SetPollAbortControllerAction
  | AbortPollingAction;

/*
 * 1° auth: AUTHORIZE start auth session with cookies
 */
export const startAuthorize =
  (codeVerifier: string, nonce: string, state: string, redirect_uri: string) =>
  async (dispatch: Dispatch<AuthorizeActionTypes>) => {
    const clientId = getEnvVar("VITE_CLIENT_ID");
    const codeChallenge = await generateCodeChallenge(codeVerifier); // Generate the code_challenge
    window.location.href = `${
      getApiEndpoints().AUTHORIZE
    }?clientSource=WEB&response_type=code&scope=openid+email+profile&client_id=${clientId}&redirect_uri=${redirect_uri}&nonce=${nonce}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

    /*
    await axios
      .get(getApiEndpoints().AUTHORIZE, {
        headers: {
          "x-client-source": "WEB",
          "x-client-risk": "test", //TBD
        },
        params: {
          response_type: "code",
          scope: "openid email profile",
          client_id: clientId,
          redirect_uri:
            "https://microservices-nova.corp.gi4e.info/logincallback",
          nonce,
          state,
          code_challenge: codeChallenge,
          code_challenge_method: "S256",
        },
        withCredentials: true,
      })
      .then((response) => {
        window.location.href = response.headers.location;
      })
      .catch((error) => console.error(error));  */
  };

/*
 * 2° auth: AUTHORIZE_2FA return a 302 and call automatically AUTHORIZE that return callback to /logincallback
 */

/*
 * for QR code flow
 */
export const authorizeUserQrCode =
  (OTP: string, operationId: string, mfaData: object, lang: string) =>
  async (dispatch: Dispatch<AuthorizeActionTypes>) => {
    dispatch({ type: AUTHORIZE_REQUEST });
    const requestConfig = {
      headers: {
        "x-client-source": "WEB",
        "x-mfa-otp": OTP,
      },
      withCredentials: true,
    };

    const requestData = {
      operationId,
      mfaData,
    };
    axios
      .post(getApiEndpoints().AUTHORIZE_2FA, requestData, requestConfig)
      .then((response) => {
        if (response.data.code === AUTH1302) {
          window.location.href = `${response.data.description}&locale=${lang}`;
        }
      })
      .catch((error) => {
        const errcode = error.response?.data?.errcode;
        if (errcode === ErrorType.sessionExpired) {
          dispatch({
            type: AUTHORIZE_SESSION_EXPIRED,
            error: errcode,
          });
        } else {
          dispatch({
            type: AUTHORIZE_FAILURE,
            error: errcode || error,
          });
        }
      });
  };

/*
 * for notification Polling flow
 */
export const authorizeUserPolling =
  (operationId: string, mfaData: object, lang: string) =>
  async (dispatch: Dispatch<AuthorizeActionTypes>) => {
    dispatch({ type: AUTHORIZE_REQUEST });
    
    // Crea un nuovo AbortController per questo polling
    const abortController = new AbortController();
    dispatch({ 
      type: SET_POLL_ABORT_CONTROLLER, 
      payload: abortController 
    });
    
    const requestConfig = {
      headers: {
        "x-client-source": "WEB",
        "x-type": "POLL",
      },
      withCredentials: true,
    };

    const requestData = {
      operationId,
      mfaData,
    };

    const poll = async (maxRetries = 10, delay = 5500) => {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        // Verifica se il polling è stato abortito
        if (abortController.signal.aborted) {
          console.log("Polling aborted");
          return;
        }
        
        try {
          const response = await axios.post(
            getApiEndpoints().AUTHORIZE_2FA,
            requestData,
            requestConfig
          );
          if (response.data.code === AUTH1302) {
            window.location.href = `${response.data.description}&locale=${lang}`;
            return;
          }
          // Qualsiasi altro 200 termina il polling
          break;
        } catch (error: any) {
          // Se il polling è stato abortito, esci senza errore
          if (abortController.signal.aborted) {
            console.log("Polling aborted during request");
            return;
          }
          
          const errcode = error?.response?.data?.errcode;
          // Se 500 con POLL_PENDING, continua polling
          if (errcode === POLL_PENDING) {
            await new Promise((res) => setTimeout(res, delay));
            continue;
          }
          // Altri errori: dispatch e termina
          dispatch({
            type: AUTHORIZE_FAILURE,
            error: errcode || error,
          });
          return;
        }
      }
      // Se superi i tentativi massimi
       dispatch({ type: AUTHORIZE_REQUEST_EXPIRED });
    };
    poll();
  };

/*
 * Abort the current polling
 */
export const abortPolling = () => (dispatch: Dispatch) => {
  console.log("abortPolling: action is dispatching");
  dispatch({ type: ABORT_POLLING });
};

/*
 * only for physical token flow
 */
export const authorizeUserPhy =
  (
    OTP: string,
    encryptedHashOtp: string,
    operationId: string,
    mfaData: object,
    lang: string
  ) =>
  async (dispatch: Dispatch<AuthorizeActionTypes>) => {
    dispatch({ type: AUTHORIZE_REQUEST });
    const requestConfig = {
      headers: {
        "x-client-source": "WEB",
        "x-mfa-otp": OTP,
        "x-mfa-hashOtp": encryptedHashOtp,
      },
      withCredentials: true,
    };

    const requestData = {
      operationId,
      mfaData,
    };

    // Flusso standard (senza polling)
    axios
      .post(getApiEndpoints().AUTHORIZE_2FA, requestData, requestConfig)
      .then((response) => {
        if (response.data.code === AUTH1302) {
          window.location.href = `${response.data.description}&locale=${lang}`;
        }
      })
          .catch((error) => {
        const errcode = error.response?.data?.errcode;
        if (errcode === ErrorType.sessionExpired) {
          dispatch({
            type: AUTHORIZE_SESSION_EXPIRED,
            error: errcode,
          });
        } else {
          dispatch({
            type: AUTHORIZE_FAILURE,
            error: errcode || error,
          });
        }
      });
  };
